import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { profileGaps, type HelpTab, type ProjectSummary, type SiteResponse } from "@joogoos/contracts";
import { api } from "../api.js";
import { Icon, type IconName } from "../ui/icons.js";
import { useProfile, useProjects } from "../ui/reads.js";
import { useWorkspace } from "../ui/workspace.js";
import { useNarrow } from "../ui/narrow.js";
import { statusLabel, when } from "../studio/Rail.js";
import { useReview } from "../review/Review.js";
import { ReviewEntry } from "../review/ReviewEntry.js";
import { Preparing, Waiting, hasWaiting, useWaiting } from "../waiting/Waiting.js";
import { HEADLINE, KICKER, TabBar, UNDER } from "./chrome.js";

/** The window the website count is read over — the same one Analytics opens on, so the two share one answer. */
const DAYS = 30;
/** Enough of the latest to see where you were, never enough to be the Content tab. */
const LATEST = 5;

/** The tabs the shell draws: `HelpTab`, plus the one that has no help page of its own yet (23). */
type TabName = HelpTab | "Customers";

export interface OverviewProps {
  onTab: (tab: TabName) => void;
  /** Open a project in the Studio, the way "Make this" on a website idea does (ADR 0035). */
  onOpen: (projectId: string) => void;
  /** Credits, in the account dialog: one of the two doors on this screen that is not a tab. */
  onCredits: () => void;
  /** The marketing review, the other (35, ADR 0063): the screen under this one, at `/overview/review`. */
  onReview: () => void;
}

type Todo = { tone: "bad" | "warn" | ""; title: string; detail: string; go: string; act: () => void };

/**
 * Overview: the first tab (the owner, 2026-09-20), the same shape Overbill's has — what needs you, then where
 * things have got to, each part a door to the tab that owns it.
 *
 * **It holds no number of its own.** Every read is one another screen already makes: the business page Business
 * draws, the worklist the Studio's rail draws, the website count Analytics draws, the balance in the header, and the
 * marketing review the screen under this one draws. So the Overview can never say something the screen it points at
 * would contradict, and it costs two round trips more than the shell already pays (docs/17 §performance 4) — three
 * since the morning list (36, ADR 0064), which is every tool's waiting rows read at once.
 *
 * **The morning list is under what needs you** (36): the letters, posts, articles and topics waiting for a yes, each
 * said yes or no to here through its own tool's route. *Needs you* stays what it was — the one thing that blocks the
 * rest — and says *All clear* only when the list is empty too. Under the list, always, is what Joogoos may spend a
 * week preparing it overnight (ADR 0064 point 5): the owner's number to set, and an editor's to read.
 *
 * **The marketing review comes first** (35, ADR 0063), on the welcome and here alike: the loop the product is being
 * rebuilt around — what happened, one thing to do, a yes, and a look at what came of it — starts on this tab, so its
 * card is the first thing on it. A recommendation waiting for the owner's word goes on the owner's *Needs you*, because
 * it does, and when it is the first thing there the card steps aside rather than say it twice. An editor's list leaves
 * it off — it is not theirs to give — and the card tells them it waits for the owner.
 *
 * **It is laid out like Analytics, because it is the same kind of screen** (2026-09-20, the owner: "make it look
 * good"). One kicker, the strongest true sentence in display type, one line under it and one coral button — then
 * the rest of what needs doing as a short list, with the standing numbers down the side as quiet rows. No boxes of
 * counters: the tab that had them lost them the same week.
 *
 * **Until something has been made, the screen is the explanation.** A person signing in for the first time meets
 * seven tabs, a chat box and a credit balance, and nothing on screen says what any of it is for — so until there
 * is work of their own to lead with, the Overview is what Joogoos is, the five steps of the work, and what each
 * tab holds. Afterwards the same words are one press away, under How this works, rather than gone for good.
 *
 * What it says about a tab is what that tab really does today, including the two that are still a design (06): a
 * welcome screen promising a content library and scheduled posts would be the first thing the product lied about.
 */
export function Overview({ onTab, onOpen, onCredits, onReview }: OverviewProps) {
  const profile = useProfile();
  const projects = useProjects();
  const ws = useWorkspace();
  const site = useQuery({ queryKey: ["site", DAYS, null, null], queryFn: () => api.site(DAYS), staleTime: 30_000 });
  const review = useReview();
  const waitingList = useWaiting();
  const listed = hasWaiting(waitingList.data);
  const [guide, setGuide] = useState(false);
  const narrow = useNarrow();

  const p = profile.data?.profile ?? { sections: [], updatedAt: null };
  const told = p.sections.filter((s) => s.body.trim()).length > 0;
  const gaps = profileGaps(p);
  const rows = [...(projects.data ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  // A draft is a conversation, not a thing that exists yet: the Overview counts what was made apart from what was
  // started, because "3 posts" that are three abandoned chats is the number every dashboard like this gets wrong.
  const made = rows.filter((r) => r.status !== "chatting");
  const drafts = rows.filter((r) => r.status === "chatting");
  const needy = rows.filter((r) => r.needs);
  const balance = ws.data?.balanceUsd;
  // Waiting on the owner's word, and on *Needs you* only for the owner: an editor cannot give it (`mayDecide`), so to
  // them it is not a thing that needs them, and the card above says whose it is instead.
  const waiting = review.data?.mayDecide && review.data.latest?.decision?.state === "proposed" ? review.data.latest.decision.action : null;

  // What needs the person, most blocking first: a Studio that does not know the business, then work of theirs that
  // stopped, then the things that make the next post better, then the money that has to be there for any of it.
  const todo: Todo[] = [];
  if (!told) {
    todo.push({
      tone: "bad", title: "Tell Joogoos about your business",
      detail: "Its name and town, or your website. Everything the Studio writes is written from that one page.",
      go: "Business", act: () => onTab("Business"),
    });
  } else if (gaps.length) {
    todo.push({
      tone: "warn", title: `Your business page is short of ${names(gaps.map((g) => g.heading.toLowerCase()))}`,
      detail: "Those are the parts the Studio writes from. Until they are answered it is making anybody's posts.",
      go: "Finish it", act: () => onTab("Business"),
    });
  }
  // A recommendation nobody has said yes or no to: only the owner can, and the next review waits on it.
  const decide: Todo | null = waiting ? {
    tone: "warn", title: "Your marketing review is waiting for your word",
    detail: `It recommends: ${waiting.title}. Say yes, change it, or say no — saying yes spends nothing; it opens the work where it is done.`,
    go: "Read the review", act: onReview,
  } : null;
  if (decide) todo.push(decide);
  for (const r of needy.slice(0, 3)) {
    todo.push({
      tone: r.status === "over_budget" ? "bad" : "warn", title: `${r.title} — ${r.needs}`,
      detail: r.status === "over_budget"
        ? "It stopped at the cap on what one project may spend. Open it to see what was made, and what it would take to finish."
        : "A conversation nobody came back to. Open it and carry on, or delete it from the list.",
      go: "Open it", act: () => onOpen(r.id),
    });
  }
  if (told && !rows.length) {
    todo.push({
      tone: "", title: "Make your first post",
      detail: "Say in a sentence what it should be about. The plan says what it costs before anything runs.",
      go: "Studio", act: () => onTab("Studio"),
    });
  }
  if (site.data && !site.data.connected) {
    todo.push({
      tone: "", title: "Count your own website",
      detail: "One line in your site says how many people came, what they came to look at and who picked up the phone — so the next post can be about what people already want.",
      go: "Analytics", act: () => onTab("Analytics"),
    });
  }
  if (balance !== undefined && Number(balance) < 1) {
    todo.push({
      tone: "warn", title: `$${balance} of credit left`,
      detail: "Credits are dollars, spent on what you make and listed call by call. Nothing can be made once they are gone.",
      go: "Credits", act: onCredits,
    });
  }

  // Nothing made yet: the screen is the explanation. A workspace with drafts in it and nothing finished is still
  // somebody who has not seen this product work, so a draft does not count as having made something. Nothing from
  // *Needs you* is repeated there — each of its rows is one of the steps, said better and in order.
  if (!made.length) {
    return (
      <Welcome
        done={told && !gaps.length} onTab={onTab} onCredits={onCredits} balance={balance}
        review={<ReviewEntry review={review.data} onOpen={onReview} />}
        waiting={<div className="flex flex-col gap-4"><Waiting onTab={onTab} onOpen={onOpen} /><Preparing /></div>}
      />
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <TabBar title={ws.data?.name ?? "Overview"} note="what needs you, and how the marketing is going" />
      <div className="flex-1 min-h-0 overflow-y-auto px-4 md:px-6 py-5 md:py-7">
        {/* Said once: when the review's decision is the screen's own sentence, under *Needs you*, the card above it
            would only say it again. In every other state — none yet, being written, decided — the card leads. */}
        {decide && todo[0] === decide ? null : <ReviewEntry review={review.data} onOpen={onReview} className="mb-8" />}
        <div className={`grid gap-y-8 ${narrow ? "grid-cols-1" : "grid-cols-[minmax(0,7fr)_minmax(0,4fr)] gap-x-12"}`}>
          <div className="min-w-0 flex flex-col gap-8">
            <Next todo={todo} listed={listed} />
            <div className="flex flex-col gap-4">
              <Waiting onTab={onTab} onOpen={onOpen} />
              <Preparing />
            </div>
            <Latest rows={rows.slice(0, LATEST)} onOpen={onOpen} onTab={onTab} />
          </div>
          <Standing
            made={made.length} drafts={drafts.length} site={site.data ?? null} balance={balance}
            onTab={onTab} onCredits={onCredits}
          />
        </div>

        {/* The welcome is not thrown away once there is work: somebody who never read it, or who forgot which tab
            holds what, should not have to make an empty workspace to find out. */}
        <section className="mt-10 pt-5 border-t border-line">
          <button
            onClick={() => setGuide((g) => !g)}
            aria-expanded={guide}
            className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[12.5px] font-semibold text-muted hover:text-ink"
          >
            <Icon name={guide ? "chevronDown" : "chevronRight"} size={14} /> How this works
            {guide ? null : <span className="font-normal text-faint">what Joogoos does, and what each tab is for</span>}
          </button>
          {guide ? (
            <div className="mt-7 flex flex-col gap-10 max-w-[900px]">
              <Steps done={told && !gaps.length} onTab={onTab} />
              <Tabs onTab={onTab} />
              <Footnote />
            </div>
          ) : null}
        </section>
      </div>
    </div>
  );
}

/* ── Nothing made yet: the product introduces itself ─────────────────────────────────────────────────────────── */

/**
 * The first screen of a new workspace, and the only place the product says what it is. It is built like the
 * public site's own pages rather than like a tool — an eyebrow, one line of display type, a lede — because this is
 * the one screen whose whole job is to be read.
 */
function Welcome({ done, onTab, onCredits, balance, review, waiting }: {
  done: boolean; onTab: (tab: TabName) => void; onCredits: () => void;
  /** What is left to spend, from the header's read; undefined until it is in. */
  balance: string | undefined;
  /** The marketing review's card, above the welcome: a new business can ask what to do first as well as anybody. */
  review: React.ReactNode;
  /**
   * The morning list (36): nothing made in the Studio does not mean nothing waits — the Customers loop writes letters
   * and Plan topics proposes articles before anybody makes a post — so it is above the welcome as well.
   */
  waiting: React.ReactNode;
}) {
  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-[900px] px-5 md:px-8 py-10 md:py-14 flex flex-col gap-12">
        {review}
        {waiting}
        <header>
          <span className={`${KICKER} inline-flex items-center gap-2 text-coral`}>
            <span className="w-1.5 h-1.5 rounded-full bg-coral" aria-hidden="true" /> Welcome to Joogoos
          </span>
          <h1 className="font-display text-[30px] md:text-[40px] leading-[1.05] font-bold tracking-[-.03em] text-balance mt-3">
            The whole of your marketing, <span className="text-coral">in one place.</span>
          </h1>
          <p className="text-[14px] md:text-[15px] text-ink-2 leading-relaxed mt-4 max-w-[62ch]">
            Joogoos does the marketing a small business never has time for. It learns what you sell and how you
            sound, makes the posts and the videos, counts who comes to your website and what they came for, and goes
            looking for customers you have not met yet. There is no subscription — you pay for what you make, the
            Studio shows a post's price before it is made, and every charge is listed, call by call.
          </p>
          {/* Said here because the first step below is itself a paid call, and an account starts empty (ADR 0048):
              somebody who reads the five steps and starts on step one should not meet a refusal for their trouble.
              Only to somebody who would: it asked a business with $10.65 to add credit (the first pilot round,
              2026-10-05), so with a dollar or more it says what there is instead — the same line *Needs you* draws
              its top-up at — and nothing until the balance is in. */}
          {balance === undefined ? null : Number(balance) < 1 ? (
            <p className="text-[13px] text-ink-2 leading-relaxed mt-3 max-w-[62ch]">
              <button onClick={onCredits} className="font-bold underline decoration-coral underline-offset-2 hover:text-coral">
                Start by adding credit
              </button>, from $10, in Credits under your picture. Credit is dollars: it is spent
              per step, on what you make, and nothing is taken monthly.
            </p>
          ) : (
            <p className="text-[13px] text-ink-2 leading-relaxed mt-3 max-w-[62ch]">
              You have ${balance} of credit, in Credits under your picture. Credit is dollars: it is spent per step, on
              what you make, and nothing is taken monthly.
            </p>
          )}
        </header>

        <Steps done={done} onTab={onTab} />
        <Tabs onTab={onTab} />
        <Footnote />
      </div>
    </div>
  );
}

/**
 * The marketing loop, in the order somebody works it: know the business, say it, be findable, see what it did, go
 * and find people. It is five steps and not the one it opened with because making a post is a fifth of marketing,
 * not the whole of it — the screen used to say the Studio was the product, which is what the Overview is for
 * correcting (the owner, 2026-09-20). A step is added here when a tab is, and not before it does something.
 *
 * Each one says what it does not do as plainly as what it does. A finished post goes out through a connected channel
 * at the person's word, or is downloaded and posted by hand (25, ADR 0038); a platform this box has no keys for is
 * "not on this box yet", and somebody who finds that out after paying for a video has been misled by our silence.
 */
const STEPS: ReadonlyArray<{ title: string; body: string; tab?: TabName }> = [
  {
    title: "Say who you are, once",
    body: "Business is one field, not a form. Give the name and the town, a website, or just the idea; Joogoos reads what it can find, writes a first draft of one page, and you check it. Everything below is written from that page, so it is the few minutes that pay for themselves.",
    tab: "Business",
  },
  {
    title: "Make what you want to say",
    body: "In the Studio a sentence is enough — something about Saturday's cinnamon buns. It asks whether it is a post or a video, which photos, and whether anyone speaks, shows the plan and the price, and makes it. A finished post goes out at your word — press Post, then now or a day and a time — to the accounts you have connected under Business → Channels, or you download the file and put it up yourself. A platform this box has no keys for says so: not on this box yet.",
    tab: "Studio",
  },
  {
    title: "Be there when they search",
    body: "SEO writes articles for your own website from your own sources — what you sell, what you have said, what you have been asked — you read and approve each one, and it is published on your domain, so the search credit is yours rather than ours.",
    tab: "SEO",
  },
  {
    title: "See who came, and what they came for",
    body: "One line in your own website counts the people it brings: how many, who sent them, which page they read and who picked up the phone. It ends in a sentence about what to make next, with the button that starts it.",
    tab: "Analytics",
  },
  {
    title: "Go and find the ones who have not heard of you",
    body: "Customers looks for businesses worth writing to, writes the letter in your words and sends it from your own mailbox, one letter to each company. You read what goes out, and replies come to your own inbox.",
    tab: "Customers",
  },
];

/**
 * The four steps as a sequence rather than four boxes: the numeral is the first thing in the row, everything hangs
 * off one hairline, and the door is a coral word instead of a button. That is how the site draws its own process,
 * and it is what stops this reading as a form to fill in.
 */
function Steps({ done, onTab }: { done: boolean; onTab: (tab: TabName) => void }) {
  return (
    <section>
      <h2 className={`${KICKER} text-muted`}>How it goes</h2>
      <ol aria-label="How it goes" className="mt-3 grid md:grid-cols-2 gap-x-12">
        {STEPS.map((s, i) => {
          const ticked = i === 0 && done;
          return (
            <li key={s.title} className="border-t border-line py-4 flex gap-4">
              <span
                aria-hidden="true"
                className={`shrink-0 w-7 font-display text-[21px] leading-none font-bold tabular-nums pt-px ${ticked ? "text-good" : "text-line-2"}`}
              >
                {ticked ? <Icon name="check" size={19} /> : `0${i + 1}`}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold tracking-[-.01em]">{s.title}</span>
                <span className="block text-[12.5px] text-muted leading-relaxed mt-1">{s.body}</span>
                {s.tab ? (
                  <button
                    onClick={() => onTab(s.tab!)}
                    aria-label={`Open ${s.tab}`}
                    className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-coral hover:underline underline-offset-2"
                  >
                    {s.tab} <span aria-hidden="true">→</span>
                  </button>
                ) : null}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/**
 * What each tab is for, in one line, as it is today — the parts of the marketing job, named in the order the bar
 * puts them. `yet` is the honest half: Content's music, caption styles and overlays are designs (06), and the Calendar
 * shows real scheduled posts (25) but cannot hold a plan yet; each tab says so when it is opened, so the welcome
 * says it too rather than sending somebody to find out.
 */
const WHAT: ReadonlyArray<{ tab: TabName; job: string; icon: IconName; line: string; yet?: string }> = [
  { tab: "Overview", job: "Where you are", icon: "home", line: "This page: what needs you, what you have made, and how the rest of it works." },
  { tab: "Business", job: "Who you are", icon: "shop", line: "What Joogoos knows about you — what you sell, who buys it, how you sound. Read it, correct it, add to it whenever something changes." },
  { tab: "Studio", job: "Making it", icon: "studio", line: "Where things are made: one conversation a post, the plan and the price before it runs, and every step shown while it happens." },
  { tab: "Content", job: "What it is made of", icon: "content", line: "Everything you have made, newest first, and the photos and clips it is made of. A video made somewhere else can be imported and posted from here.", yet: "Music, caption styles and overlays to choose from are not built yet." },
  { tab: "Calendar", job: "When it goes out", icon: "calendar", line: "What is scheduled, by month, week or list: a card per post per channel, coloured by what happened to it.", yet: "A post is scheduled from the Studio once it is made. Writing a plan on a day is not built yet." },
  { tab: "Analytics", job: "What it did", icon: "analytics", line: "What worked. Your own website is counted — who came, what they looked at, who got in touch — and it ends in what to make next.", yet: "Views and followers wait on a channel connection." },
  { tab: "SEO", job: "Being found", icon: "seo", line: "Blog articles for your own website, written from your own sources and published on your own domain, so what they earn in search is yours. Nothing goes out until you have read it.", yet: "WordPress, Shopify and Wix sites cannot be published to yet." },
  { tab: "Customers", job: "Finding new people", icon: "users", line: "Finding this business its next customer: companies worth writing to, and the letter that goes out to each. Letters leave from your own mailbox, never a Joogoos one, and replies come to your own inbox." },
];

function Tabs({ onTab }: { onTab: (tab: TabName) => void }) {
  return (
    <section>
      <h2 className={`${KICKER} text-muted`}>What each tab holds</h2>
      <ul aria-label="What each tab holds" className="mt-3 grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(265px,1fr))]">
        {WHAT.map((w) => (
          <li key={w.tab} className="contents">
            <button
              onClick={() => onTab(w.tab)}
              aria-label={`Open ${w.tab}`}
              className="h-full text-left bg-panel border border-line rounded-[12px] p-4 flex flex-col gap-2.5 transition hover:border-line-2 hover:shadow-[0_6px_18px_rgba(23,24,23,.06)]"
            >
              <span className="flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-[9px] bg-tint text-coral grid place-items-center shrink-0" aria-hidden="true">
                  <Icon name={w.icon} size={15} />
                </span>
                <span className="min-w-0">
                  <span className="block text-[9.5px] font-bold uppercase tracking-[.08em] text-faint">{w.job}</span>
                  <span className="block text-[13.5px] font-semibold tracking-[-.01em]">{w.tab}</span>
                </span>
              </span>
              <span className="block text-[12.5px] text-muted leading-relaxed">{w.line}</span>
              {w.yet ? (
                <span className="mt-auto flex items-start gap-1.5 text-[11.5px] text-faint leading-relaxed">
                  <span className="mt-[6px] w-1 h-1 rounded-full bg-line-2 shrink-0" aria-hidden="true" />
                  {w.yet}
                </span>
              ) : null}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The three things true of every screen, said once, the way the site says its promise: one quiet strip. */
const PROMISES = [
  "Credits are dollars, and they are yours rather than the workspace's",
  "Nothing is charged until you press the button",
  "Help, under your picture in Settings, answers and takes a problem to a person",
];

function Footnote() {
  return (
    <p className="border-t border-line pt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-muted">
      {PROMISES.map((promise, i) => (
        <span key={promise} className="inline-flex items-center gap-3">
          {i ? <span className="text-coral text-[9px]" aria-hidden="true">✳</span> : null}
          {promise}
        </span>
      ))}
    </p>
  );
}

/* ── Once there is work ──────────────────────────────────────────────────────────────────────────────────────── */

/**
 * What needs you. The most blocking one is the screen's sentence, in display type with the one coral button, and
 * the rest are a short list under it — the shape Analytics uses for a finding, because the question is the same
 * one: *of everything true right now, which is the thing to do?*
 */
function Next({ todo, listed }: { todo: Todo[]; listed: boolean }) {
  const [first, ...rest] = todo;
  if (!first) {
    // The list under this says what waits; "All clear" over it would be the screen contradicting itself.
    if (listed) return null;
    return (
      <section>
        <h2 className={`${KICKER} text-muted`}>All clear</h2>
        <p className={HEADLINE}>Nothing needs you.</p>
        <p className={UNDER}>
          Everything you have started is finished or running. A letter, a post or an article waiting for your yes, a
          reply from someone you wrote to, a draft left unfinished or work stopped at its cap appears here by itself.
        </p>
      </section>
    );
  }
  return (
    <section>
      <h2 className={`${KICKER} ${first.tone === "" ? "text-muted" : "text-coral"}`}>
        {rest.length ? `Needs you · ${rest.length + 1}` : "Needs you"}
      </h2>
      <p className={HEADLINE}>{first.title}</p>
      <p className={UNDER}>{first.detail}</p>
      <button
        onClick={first.act}
        className="mt-3.5 inline-flex items-center gap-1.5 bg-coral text-white rounded-lg px-3.5 py-2 text-[13px] font-semibold"
      >
        {first.go} <span aria-hidden="true">→</span>
      </button>
      {rest.length ? (
        <ul aria-label="Next" className="mt-5 flex flex-col">
          {rest.map((t) => (
            <li key={t.title} className="border-t border-line">
              <button onClick={t.act} className="w-full py-2.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 text-left group">
                <span className="text-[13.5px]">{t.title}</span>
                <span className="shrink-0 text-[12.5px] font-semibold text-coral group-hover:underline underline-offset-2">
                  {t.go} <span aria-hidden="true">→</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/** The latest work, as lines rather than a stack of cards: it is a way back in, not a gallery. */
function Latest({ rows, onOpen, onTab }: { rows: ProjectSummary[]; onOpen: (id: string) => void; onTab: (tab: TabName) => void }) {
  return (
    <section>
      <div className="flex items-baseline justify-between gap-3">
        <h2 className={`${KICKER} text-muted`}>Latest</h2>
        <button
          onClick={() => onTab("Studio")}
          aria-label="Open the Studio"
          className="text-[12px] font-semibold text-coral hover:underline underline-offset-2"
        >
          Studio <span aria-hidden="true">→</span>
        </button>
      </div>
      <ul aria-label="Latest" className="mt-2 flex flex-col">
        {rows.map((r) => (
          <li key={r.id} className="border-t border-line">
            <button onClick={() => onOpen(r.id)} className="w-full py-2.5 flex items-center gap-3 text-left group">
              <Icon name={r.kind === "post" ? "image" : "play"} size={14} className="text-faint shrink-0 group-hover:text-coral" />
              <span className="min-w-0 flex-1 text-[13.5px] truncate">{r.title}</span>
              <span className="shrink-0 text-[11.5px] text-faint">{r.needs ?? statusLabel(r.status)} · {when(r.updatedAt)}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Where things stand, down the side: three quiet rows, each a door to the tab the number came from. It was a row
 * of four counters until 2026-09-20, when Analytics lost its own row of boxes at the owner's word — "a lot of lost
 * space, not very pro" — and four tiles at the top of the first tab would have been that screen rebuilt one tab
 * along.
 */
function Standing({ made, drafts, site, balance, onTab, onCredits }: {
  made: number; drafts: number; site: SiteResponse | null; balance: string | undefined;
  onTab: (tab: TabName) => void; onCredits: () => void;
}) {
  const s = site?.summary ?? null;
  const counted = s && site?.domain;
  const rows: Array<{ label: string; value: string; note: string; act: () => void }> = [
    {
      label: "Made", value: String(made),
      note: drafts ? `${drafts} draft${drafts === 1 ? "" : "s"} on the go` : "no draft open",
      act: () => onTab("Studio"),
    },
    {
      label: "On your website", value: counted ? String(s.visitors) : "—",
      note: counted
        ? `${s.visitors === 1 ? "person" : "people"} in ${DAYS} days${s.enquiries ? ` · ${s.enquiries} got in touch` : ""}`
        : "not counted yet",
      act: () => onTab("Analytics"),
    },
    { label: "Credit left", value: balance ? `$${balance}` : "—", note: "credits are dollars", act: onCredits },
  ];
  return (
    <section className="min-w-0">
      <h2 className={`${KICKER} text-muted`}>Where things stand</h2>
      <ul className="mt-2 flex flex-col">
        {rows.map((r) => (
          <li key={r.label} className="border-t border-line">
            <button onClick={r.act} className="w-full py-3 flex items-baseline gap-3 text-left group">
              <span className="min-w-0 flex-1">
                <span className="block text-[12.5px] font-medium group-hover:text-coral">{r.label}</span>
                <span className="block text-[11.5px] text-faint mt-0.5">{r.note}</span>
              </span>
              <span className="shrink-0 font-display text-[20px] font-bold tabular-nums leading-none">{r.value}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** "what we sell and who buys it" — a list a person would say out loud, not a comma-separated one. */
function names(all: string[]): string {
  if (all.length <= 1) return all[0] ?? "";
  return `${all.slice(0, -1).join(", ")} and ${all[all.length - 1]}`;
}
